import SpineWrapper from '@Core/Animation/SpineWrapper';
import { EWildWheelFeatureStates } from '@Core/WildWheelFeature/States/WildWheelFeatureStates';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { DeferredPromise } from '@Tools/DeferredPromise';

export enum ESpineAnimationSkipMode {
    NONE = 'NONE',
    CURRENT = 'CURRENT',
    ALL = 'ALL',
    TO = 'TO'
}

export type ISpineAnimationSkipConfig =
    | {
          mode: ESpineAnimationSkipMode.NONE | ESpineAnimationSkipMode.CURRENT | ESpineAnimationSkipMode.ALL;
      }
    | {
          mode: ESpineAnimationSkipMode.TO;
          target: EGameEvents | EWildWheelFeatureStates;
          targetIds?: number[];
      };

export interface ISpineAnimationStepContext {
    event: EGameEvents | EWildWheelFeatureStates;
    eventIds?: number[];
    spineWrapper: SpineWrapper;
    waitForAnimationEndSkippable: () => Promise<void>;
}

export interface ISpineAnimationStep {
    event: EGameEvents | EWildWheelFeatureStates;
    eventIds?: number[];
    spineWrapper: SpineWrapper;
    skip?: ISpineAnimationSkipConfig;
    handler?: (context: ISpineAnimationStepContext) => Promise<void>;
    onComplete?: (context: ISpineAnimationStepContext) => void | Promise<void>;
    onStart?: (context: ISpineAnimationStepContext) => void | Promise<void>;
}

export default class SpineAnimationSequence {
    private readonly skipPromise = new DeferredPromise();
    private readonly steps: ISpineAnimationStep[];
    private isFastForwarding = false;
    private currentIndex = -1;
    private skipToIndex?: number;
    private currentSkipConfig: ISpineAnimationSkipConfig = {
        mode: ESpineAnimationSkipMode.CURRENT
    };

    constructor(
        private readonly defaultSpine: SpineWrapper,
        steps: ISpineAnimationStep[]
    ) {
        this.steps = steps.filter((step) => {
            const spine = step.spineWrapper ?? defaultSpine;
            const hasAnimation = spine.eventManager.hasGameEventAnimation(step.event);
            if (!hasAnimation) {
                console.warn(
                    `[SpineAnimationSequence] Skipping animation step "${step.event}" because no matching animation was found on spine "${spine.name ?? 'unknown'}".`
                );
            }
            return hasAnimation;
        });
    }

    public async play(): Promise<void> {
        this.isFastForwarding = false;

        for (this.currentIndex = 0; this.currentIndex < this.steps.length; this.currentIndex++) {
            const step = this.steps[this.currentIndex];

            if (this.isFastForwarding && this.currentIndex === this.skipToIndex) {
                this.isFastForwarding = false;
                this.skipToIndex = undefined;
            }

            this.currentSkipConfig = step.skip ?? { mode: ESpineAnimationSkipMode.CURRENT };
            this.skipPromise.reset();

            if (this.isFastForwarding) {
                this.skipPromise.resolve();
            }

            await this.playStep(step);
        }
    }

    private async playStep(step: ISpineAnimationStep): Promise<void> {
        const spine = step.spineWrapper ?? this.defaultSpine;
        const context: ISpineAnimationStepContext = {
            event: step.event,
            eventIds: step.eventIds,
            spineWrapper: spine,
            waitForAnimationEndSkippable: () => this.waitForAnimationEndSkippable(spine, step.event)
        };

        await step.onStart?.(context);

        const defaultHandler = async ({ event, eventIds, waitForAnimationEndSkippable }: ISpineAnimationStepContext) => {
            EventsCenter.emit(event, { eventIds });
            await waitForAnimationEndSkippable();
        };

        await Promise.race([(step.handler ?? defaultHandler)(context), this.skipPromise.promise]);

        if (this.isFastForwarding) {
            spine.eventManager.applyLastFrameOfCurrentAnimation();
        }

        await step.onComplete?.(context);
    }

    public skip(): void {
        const skipConfig = this.currentSkipConfig;

        switch (skipConfig.mode) {
            case ESpineAnimationSkipMode.ALL:
                this.isFastForwarding = true;
                this.skipPromise.resolve();
                break;

            case ESpineAnimationSkipMode.CURRENT:
                this.skipPromise.resolve();
                break;

            case ESpineAnimationSkipMode.TO: {
                const targetIndex = this.steps.findIndex((step, index) => {
                    if (index <= this.currentIndex || step.event !== skipConfig.target) return false;
                    if (!skipConfig.targetIds) return true;
                    return skipConfig.targetIds.every((id, i) => step.eventIds?.[i] === id);
                });

                if (targetIndex === -1) break;

                this.skipToIndex = targetIndex;
                this.isFastForwarding = true;
                this.skipPromise.resolve();

                break;
            }

            case ESpineAnimationSkipMode.NONE:
                break;
        }
    }

    private async waitForAnimationEndSkippable(spine: SpineWrapper, event: EGameEvents | EWildWheelFeatureStates, eventIds?: number[]): Promise<void> {
        const animationCompletion = spine.waitForAnimationEvent(event, 'complete', eventIds);

        const result = await Promise.race([
            animationCompletion.promise.then(() => 'completed' as const),
            this.skipPromise.promise.then(() => 'skipped' as const)
        ]);

        animationCompletion.cancel();

        if (result === 'skipped') {
            spine.eventManager.applyLastFrameOfCurrentAnimation();
        }
    }
}

export class SpineAnimationSequenceBuilder {
    private readonly steps: ISpineAnimationStep[] = [];
    private readonly defaults: Partial<ISpineAnimationStep>;

    constructor(defaults: Omit<Partial<ISpineAnimationStep>, 'event'> = {}) {
        this.defaults = defaults;
    }

    public spineWrapper(spineWrapper: SpineWrapper): this {
        this.defaults.spineWrapper = spineWrapper;
        return this;
    }

    public skip(skip: ISpineAnimationSkipConfig): this {
        this.defaults.skip = skip;
        return this;
    }

    public handler(handler: ISpineAnimationStep['handler']): this {
        this.defaults.handler = handler;
        return this;
    }

    public onStart(onStart: ISpineAnimationStep['onStart']): this {
        this.defaults.onStart = onStart;
        return this;
    }

    public onComplete(onComplete: ISpineAnimationStep['onComplete']): this {
        this.defaults.onComplete = onComplete;
        return this;
    }

    public step(event: EGameEvents | EWildWheelFeatureStates, options: Omit<Partial<ISpineAnimationStep>, 'event'> = {}): this {
        this.steps.push({
            ...this.defaults,
            ...options,
            event
        } as ISpineAnimationStep);

        return this;
    }

    public build(): SpineAnimationSequence {
        return new SpineAnimationSequence(this.steps[0].spineWrapper, this.steps);
    }
}
